import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Bar, BarChart, Line, ComposedChart,
  PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import { fetchTableData } from '../../services/api';
import MultiSelectDropdown from '../multiselect/MultiSelectDropdown';

// ═══════════════════════════════════════════════════════════════════
// ── CONSTANTS — change SG here once, it applies everywhere ───────
// ═══════════════════════════════════════════════════════════════════
const SG = 2.72; // Specific Gravity — multiply m³ by SG to get tonnes

// ── Colour palette (dull/muted — avoid red & green which have mining meaning) ──
const COLORS = {
  actual:    '#b45309', // amber-700 (muted amber)
  planned:   '#4338ca', // indigo-700 (muted indigo)
  ore:       '#0369a1', // sky-700 (muted blue)
  waste:     '#78716c', // stone-500 (neutral grey-brown)
  variance:  '#7c3aed', // violet-600
  budget:    '#4338ca', // indigo-700
  line:      '#7c3aed', // violet-600
  onPlan:    '#4338ca', // indigo-700
  offPlan:   '#b45309', // amber-700
  notMined:  '#78716c', // stone-500
  // Ore movement colours
  directCrusher: '#0891b2', // cyan-600
  toRompad:      '#059669', // emerald-600
  fromRompad:    '#7c3aed', // violet-600
  oreInPit:      '#dc2626', // red-600
  crusher:       '#b45309', // amber-700
};
const PIE_COLORS = ['#0369a1', '#78716c', '#4338ca', '#b45309', '#7c3aed', '#6d28d9'];
const GRADE_BIN_COLORS = ['#0369a1', '#0891b2', '#059669', '#65a30d', '#ca8a04', '#ea580c', '#dc2626', '#9333ea', '#c026d3', '#4338ca'];

// ── Helpers ─────────────────────────────────────────────────────────
const fmt = (v, dec = 0) => v == null ? '—' : Number(v).toLocaleString('en', { maximumFractionDigits: dec });
const pct = (actual, planned) => planned ? (((actual - planned) / planned) * 100).toFixed(1) : '—';
const volToTonnes = (vol) => vol * SG;

const parseDateField = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? null : d;
};

const getDayKey = (d) => {
  if (!d) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const mon = d.toLocaleString('default', { month: 'short' });
  return `${day} ${mon} ${d.getFullYear()}`;
};
const getMonthKey = (d) => d ? `${d.toLocaleString('default', { month: 'short' })} ${d.getFullYear()}` : '';
const getQuarterKey = (d) => d ? `Q${Math.ceil((d.getMonth() + 1) / 3)} ${d.getFullYear()}` : '';
const getYearKey = (d) => d ? `${d.getFullYear()}` : '';

const periodFn = { Day: getDayKey, Month: getMonthKey, Quarter: getQuarterKey, Year: getYearKey };

const dayOrder = (key) => {
  const parts = key.split(' ');
  if (parts.length < 3) return 0;
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const day = parseInt(parts[0]) || 0;
  const mIdx = months.indexOf(parts[1]);
  const year = parseInt(parts[2]) || 0;
  return year * 10000 + (mIdx >= 0 ? mIdx : 0) * 100 + day;
};

const monthOrder = (key) => {
  const parts = key.split(' ');
  if (parts.length < 2) return 0;
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const mIdx = months.indexOf(parts[0]);
  return parseInt(parts[1]) * 100 + (mIdx >= 0 ? mIdx : 0);
};

const quarterOrder = (key) => {
  const m = key.match(/Q(\d)\s+(\d{4})/);
  return m ? parseInt(m[2]) * 10 + parseInt(m[1]) : 0;
};

const sortPeriodKeys = (keys, period) => {
  if (period === 'Day') return [...keys].sort((a, b) => dayOrder(a) - dayOrder(b));
  if (period === 'Month') return [...keys].sort((a, b) => monthOrder(a) - monthOrder(b));
  if (period === 'Quarter') return [...keys].sort((a, b) => quarterOrder(a) - quarterOrder(b));
  return [...keys].sort();
};

// ── Sub-tab definitions ─────────────────────────────────────────────
const SUB_TABS = [
  { key: 'mining',       label: 'Mining',        icon: '⛏️' },
  { key: 'compliance',   label: 'Compliance',    icon: '✅' },
  { key: 'oreMovement',  label: 'Ore Movement',  icon: '🚚' },
  { key: 'processing',   label: 'Processing',    icon: '🏭' },
];

// ═══════════════════════════════════════════════════════════════════
// ── Main Component ────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════
export default function BlastStatistics() {
  const [activeTab, setActiveTab] = useState('mining');

  // Raw API data
  const [actualBlasts, setActualBlasts] = useState([]);
  const [plannedBlasts, setPlannedBlasts] = useState([]);
  const [exPitToCrusher, setExPitToCrusher] = useState([]);
  const [exPitToRompad, setExPitToRompad] = useState([]);
  const [rompadToCrusher, setRompadToCrusher] = useState([]);
  const [budgetCrusher, setBudgetCrusher] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Shared filters — "period" is display grouping only; "selectedMonths" is the date filter
  const [period, setPeriod] = useState('Month');       // Group By: Day | Month | Quarter | Year
  const [selectedMonths, setSelectedMonths] = useState([]);  // Date filter (always month-based)
  const [selectedBlocks, setSelectedBlocks] = useState([]);
  const [selectedPrdIds, setSelectedPrdIds] = useState([]);

  // ── Fetch data on mount ──
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [actRes, planRes, epCrushRes, epRomRes, romCrushRes, crushBgtRes] = await Promise.all([
          fetchTableData('tblLogBlasts'),
          fetchTableData('tblBgtBlastsMonthly'),
          fetchTableData('tblLogExPitToCrusher'),
          fetchTableData('tblLogExPitToRompad'),
          fetchTableData('tblLogRompadToCrusher'),
          fetchTableData('tblBgtCrusherFeed'),
        ]);
        if (cancelled) return;

        const errors = [];
        [
          { name: 'tblLogBlasts', res: actRes },
          { name: 'tblBgtBlastsMonthly', res: planRes },
          { name: 'tblLogExPitToCrusher', res: epCrushRes },
          { name: 'tblLogExPitToRompad', res: epRomRes },
          { name: 'tblLogRompadToCrusher', res: romCrushRes },
          { name: 'tblBgtCrusherFeed', res: crushBgtRes },
        ].forEach(({ name, res }) => {
          if (!res.success) {
            console.error(`[BlastStatistics] ${name} error:`, res.error || res);
            errors.push(`${name}: ${res.error || 'Unknown error'}`);
          }
        });

        if (errors.length > 0) {
          setError('API errors: ' + errors.join(' | '));
        }

        setActualBlasts(actRes.success ? actRes.data : []);
        setPlannedBlasts(planRes.success ? planRes.data : []);
        setExPitToCrusher(epCrushRes.success ? epCrushRes.data : []);
        setExPitToRompad(epRomRes.success ? epRomRes.data : []);
        setRompadToCrusher(romCrushRes.success ? romCrushRes.data : []);
        setBudgetCrusher(crushBgtRes.success ? crushBgtRes.data : []);

        console.log('[BlastStatistics] Data loaded:', {
          actualBlasts: actRes.success ? actRes.data?.length : 0,
          plannedBlasts: planRes.success ? planRes.data?.length : 0,
          exPitToCrusher: epCrushRes.success ? epCrushRes.data?.length : 0,
          exPitToRompad: epRomRes.success ? epRomRes.data?.length : 0,
          rompadToCrusher: romCrushRes.success ? romCrushRes.data?.length : 0,
          budgetCrusher: crushBgtRes.success ? crushBgtRes.data?.length : 0,
        });
      } catch (e) {
        if (!cancelled) setError('Failed to load data: ' + e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // ── Enrich rows with display period key + month key (for filtering) + ore/waste ──
  const enriched = useMemo(() => {
    const fn = periodFn[period];
    const act = actualBlasts.map(r => {
      const d = parseDateField(r.Date);
      const isOre = r.BlastedMaterial ? r.BlastedMaterial.toLowerCase().includes('ore') : false;
      return { ...r, _date: d, _period: fn(d), _month: getMonthKey(d), _type: isOre ? 'Ore' : 'Waste', _src: 'actual' };
    });
    const plan = plannedBlasts.map(r => {
      const d = parseDateField(r.Date);
      const grade = parseFloat(r.BlastedGrade) || 0;
      return { ...r, _date: d, _period: fn(d), _month: getMonthKey(d), _type: grade > 0 ? 'Ore' : 'Waste', _src: 'planned' };
    });
    return { act, plan };
  }, [actualBlasts, plannedBlasts, period]);

  // ── Available filter options ──
  const filterOptions = useMemo(() => {
    const allAct = enriched.act;
    const allPlan = enriched.plan;
    // Month list for date filter (always month-based, independent of Group By)
    const monthSet = new Set([...allAct.map(r => r._month), ...allPlan.map(r => r._month)]);
    const blockSet = new Set([...allAct.map(r => r.BlockID).filter(Boolean), ...allPlan.map(r => r.BlockID).filter(Boolean)]);
    const prdSet = new Set([...allAct.map(r => r.PrdID).filter(Boolean), ...allPlan.map(r => r.PrdID).filter(Boolean)]);
    return {
      months: sortPeriodKeys([...monthSet].filter(Boolean), 'Month'),
      blocks: [...blockSet].sort(),
      prdIds: [...prdSet].sort(),
    };
  }, [enriched]);

  // ── Apply filters (selectedMonths = date filter, independent of display grouping) ──
  const filtered = useMemo(() => {
    const passFilter = (r) => {
      if (selectedMonths.length > 0 && !selectedMonths.includes(r._month)) return false;
      if (selectedBlocks.length > 0 && !selectedBlocks.includes(r.BlockID)) return false;
      if (selectedPrdIds.length > 0 && !selectedPrdIds.includes(r.PrdID)) return false;
      return true;
    };
    return { act: enriched.act.filter(passFilter), plan: enriched.plan.filter(passFilter) };
  }, [enriched, selectedMonths, selectedBlocks, selectedPrdIds]);

  // ── Aggregate by period (mining) ──
  const periodData = useMemo(() => {
    const map = {};
    const ensure = (key) => {
      if (!map[key]) map[key] = {
        period: key, act_volume: 0, plan_volume: 0, act_drilled: 0, plan_drilled: 0,
        act_blasts: 0, plan_blasts: 0, act_explosives: 0, act_ore_vol: 0, act_waste_vol: 0,
        plan_ore_vol: 0, plan_waste_vol: 0, plan_grade_sum: 0, plan_grade_wt: 0,
        act_pf_sum: 0, plan_pf_sum: 0, pf_count: 0, act_payable_vol: 0, act_payable_m: 0,
        act_tonnes: 0, act_ore_tonnes: 0, act_waste_tonnes: 0, plan_tonnes: 0,
      };
    };
    filtered.act.forEach(r => {
      const key = r._period; if (!key) return; ensure(key);
      const vol = parseFloat(r.BlastedVolume) || 0;
      const tonnes = volToTonnes(vol);
      map[key].act_volume += vol; map[key].act_drilled += parseFloat(r.DrilledMeters) || 0;
      map[key].act_blasts += 1; map[key].act_explosives += parseFloat(r.Explosives) || 0;
      map[key].act_payable_vol += parseFloat(r.PayableVolume) || 0;
      map[key].act_payable_m += parseFloat(r.PayableMeters) || 0;
      map[key].act_tonnes += tonnes;
      if (r._type === 'Ore') { map[key].act_ore_vol += vol; map[key].act_ore_tonnes += tonnes; }
      else { map[key].act_waste_vol += vol; map[key].act_waste_tonnes += tonnes; }
      const apf = parseFloat(r.ActualPF); const ppf = parseFloat(r.PlannedPF);
      if (!isNaN(apf)) { map[key].act_pf_sum += apf; map[key].pf_count += 1; }
      if (!isNaN(ppf)) { map[key].plan_pf_sum += ppf; }
    });
    filtered.plan.forEach(r => {
      const key = r._period; if (!key) return; ensure(key);
      const vol = parseFloat(r.BlastedVolume) || 0;
      map[key].plan_volume += vol; map[key].plan_drilled += parseFloat(r.DrilledMeters) || 0;
      map[key].plan_blasts += 1; map[key].plan_tonnes += volToTonnes(vol);
      const grade = parseFloat(r.BlastedGrade) || 0;
      if (grade > 0) { map[key].plan_ore_vol += vol; map[key].plan_grade_sum += grade * vol; map[key].plan_grade_wt += vol; }
      else map[key].plan_waste_vol += vol;
    });
    const keys = sortPeriodKeys(Object.keys(map), period);
    return keys.map(k => {
      const d = map[k];
      return { ...d,
        plan_avg_grade: d.plan_grade_wt > 0 ? d.plan_grade_sum / d.plan_grade_wt : 0,
        act_avg_pf: d.pf_count > 0 ? d.act_pf_sum / d.pf_count : 0,
        plan_avg_pf: d.pf_count > 0 ? d.plan_pf_sum / d.pf_count : 0,
        vol_variance_pct: d.plan_volume > 0 ? ((d.act_volume - d.plan_volume) / d.plan_volume * 100) : 0,
        drill_variance_pct: d.plan_drilled > 0 ? ((d.act_drilled - d.plan_drilled) / d.plan_drilled * 100) : 0,
      };
    });
  }, [filtered, period]);

  // ── Helper: does a raw row (with Date column) pass the month filter? ──
  const passMonthFilter = useCallback((r) => {
    if (selectedMonths.length === 0) return true;
    const d = parseDateField(r.Date);
    return d && selectedMonths.includes(getMonthKey(d));
  }, [selectedMonths]);

  // ═══════════════════════════════════════════════════════════════════
  // ── Crusher feed = ExPitToCrusher + RompadToCrusher (replaces tblLogCrusherFeed)
  // ═══════════════════════════════════════════════════════════════════
  const crusherData = useMemo(() => {
    const fn = periodFn[period]; const map = {};
    const ensure = (key) => {
      if (!map[key]) map[key] = {
        period: key, act_tonnes: 0, bgt_tonnes: 0,
        act_grade_sum: 0, act_grade_wt: 0, bgt_grade_sum: 0, bgt_grade_wt: 0,
        act_content: 0, bgt_content: 0,
        // Separate tracking per source
        direct_tonnes: 0, direct_grade_sum: 0, direct_grade_wt: 0, direct_content: 0,
        rompad_tonnes: 0, rompad_grade_sum: 0, rompad_grade_wt: 0, rompad_content: 0,
      };
    };
    // Direct feed from pit
    exPitToCrusher.forEach(r => {
      if (!passMonthFilter(r)) return;
      const d = parseDateField(r.Date); const key = fn(d); if (!key) return;
      ensure(key);
      const t = parseFloat(r.Tonnage) || 0;
      const g = parseFloat(r.Grade) || 0;
      const c = parseFloat(r.Content) || 0;
      map[key].act_tonnes += t; map[key].direct_tonnes += t;
      map[key].act_grade_sum += g * t; map[key].act_grade_wt += t;
      map[key].act_content += c;
      map[key].direct_grade_sum += g * t; map[key].direct_grade_wt += t;
      map[key].direct_content += c;
    });
    // Feed from rompad
    rompadToCrusher.forEach(r => {
      if (!passMonthFilter(r)) return;
      const d = parseDateField(r.Date); const key = fn(d); if (!key) return;
      ensure(key);
      const t = parseFloat(r.Tonnage) || 0;
      const g = parseFloat(r.Grade) || 0;
      const c = parseFloat(r.Content) || 0;
      map[key].act_tonnes += t; map[key].rompad_tonnes += t;
      map[key].act_grade_sum += g * t; map[key].act_grade_wt += t;
      map[key].act_content += c;
      map[key].rompad_grade_sum += g * t; map[key].rompad_grade_wt += t;
      map[key].rompad_content += c;
    });
    // Budget
    budgetCrusher.forEach(r => {
      if (!passMonthFilter(r)) return;
      const d = parseDateField(r.Date); const key = fn(d); if (!key) return;
      ensure(key);
      const t = parseFloat(r.Tonnage) || 0; const g = parseFloat(r.Grade) || 0;
      map[key].bgt_tonnes += t; map[key].bgt_grade_sum += g * t;
      map[key].bgt_grade_wt += t; map[key].bgt_content += parseFloat(r.Content) || 0;
    });
    const keys = sortPeriodKeys(Object.keys(map), period);
    return keys.map(k => {
      const d = map[k];
      return { ...d,
        act_avg_grade: d.act_grade_wt > 0 ? d.act_grade_sum / d.act_grade_wt : 0,
        bgt_avg_grade: d.bgt_grade_wt > 0 ? d.bgt_grade_sum / d.bgt_grade_wt : 0,
        direct_avg_grade: d.direct_grade_wt > 0 ? d.direct_grade_sum / d.direct_grade_wt : 0,
        rompad_avg_grade: d.rompad_grade_wt > 0 ? d.rompad_grade_sum / d.rompad_grade_wt : 0,
      };
    });
  }, [exPitToCrusher, rompadToCrusher, budgetCrusher, period, passMonthFilter]);

  // ═══════════════════════════════════════════════════════════════════
  // ── Ore Movement calculations ─────────────────────────────────────
  // ═══════════════════════════════════════════════════════════════════
  const oreMovement = useMemo(() => {
    const fn = periodFn[period];

    // --- Period-level ore mined (from blasts, converted to tonnes via SG) ---
    const oreMinedMap = {};
    filtered.act.filter(r => r._type === 'Ore').forEach(r => {
      const key = r._period; if (!key) return;
      if (!oreMinedMap[key]) oreMinedMap[key] = { vol: 0, tonnes: 0, blocks: new Set() };
      const vol = parseFloat(r.BlastedVolume) || 0;
      oreMinedMap[key].vol += vol;
      oreMinedMap[key].tonnes += volToTonnes(vol);
      if (r.BlockID) oreMinedMap[key].blocks.add(r.BlockID);
    });

    // --- Ex-pit to crusher ---
    const epCrushMap = {};
    exPitToCrusher.forEach(r => {
      if (!passMonthFilter(r)) return;
      const d = parseDateField(r.Date); const key = fn(d); if (!key) return;
      if (!epCrushMap[key]) epCrushMap[key] = { tonnes: 0, content: 0, blocks: new Set() };
      epCrushMap[key].tonnes += parseFloat(r.Tonnage) || 0;
      epCrushMap[key].content += parseFloat(r.Content) || 0;
      if (r.BlockID) epCrushMap[key].blocks.add(r.BlockID);
    });

    // --- Ex-pit to rompad ---
    const epRomMap = {};
    const romInByBin = {};   // { period: { bin: tonnes } }
    exPitToRompad.forEach(r => {
      if (!passMonthFilter(r)) return;
      const d = parseDateField(r.Date); const key = fn(d); if (!key) return;
      if (!epRomMap[key]) epRomMap[key] = { tonnes: 0, content: 0, blocks: new Set() };
      const t = parseFloat(r.Tonage) || parseFloat(r.Tonnage) || 0; // DB has typo "Tonage"
      epRomMap[key].tonnes += t;
      epRomMap[key].content += parseFloat(r.Content) || 0;
      if (r.BlockID) epRomMap[key].blocks.add(r.BlockID);
      // By grade bin
      const bin = r.GradeBin || 'Unknown';
      if (!romInByBin[key]) romInByBin[key] = {};
      romInByBin[key][bin] = (romInByBin[key][bin] || 0) + t;
    });

    // --- Rompad to crusher ---
    const romCrushMap = {};
    const romOutByBin = {};  // { period: { bin: tonnes } }
    rompadToCrusher.forEach(r => {
      if (!passMonthFilter(r)) return;
      const d = parseDateField(r.Date); const key = fn(d); if (!key) return;
      if (!romCrushMap[key]) romCrushMap[key] = { tonnes: 0, content: 0 };
      const t = parseFloat(r.Tonnage) || 0;
      romCrushMap[key].tonnes += t;
      romCrushMap[key].content += parseFloat(r.Content) || 0;
      const bin = r.GradeBin || 'Unknown';
      if (!romOutByBin[key]) romOutByBin[key] = {};
      romOutByBin[key][bin] = (romOutByBin[key][bin] || 0) + t;
    });

    // --- Collect all periods and grade bins ---
    const allPeriods = new Set([
      ...Object.keys(oreMinedMap), ...Object.keys(epCrushMap),
      ...Object.keys(epRomMap), ...Object.keys(romCrushMap),
    ]);
    const sortedPeriods = sortPeriodKeys([...allPeriods], period);

    const allGradeBins = new Set();
    Object.values(romInByBin).forEach(bins => Object.keys(bins).forEach(b => allGradeBins.add(b)));
    Object.values(romOutByBin).forEach(bins => Object.keys(bins).forEach(b => allGradeBins.add(b)));
    const gradeBins = [...allGradeBins].sort();

    // --- Period rows ---
    const periodRows = sortedPeriods.map(k => {
      const mined = oreMinedMap[k] || { vol: 0, tonnes: 0 };
      const epC = epCrushMap[k] || { tonnes: 0 };
      const epR = epRomMap[k] || { tonnes: 0 };
      const rC = romCrushMap[k] || { tonnes: 0 };
      const totalMoved = epC.tonnes + epR.tonnes;
      const oreInPit = mined.tonnes - totalMoved;
      return {
        period: k,
        ore_mined_vol: mined.vol,
        ore_mined_tonnes: mined.tonnes,
        expit_to_crusher: epC.tonnes,
        expit_to_rompad: epR.tonnes,
        rompad_to_crusher: rC.tonnes,
        total_moved: totalMoved,
        total_to_crusher: epC.tonnes + rC.tonnes,
        ore_in_pit: oreInPit,
      };
    });

    // --- Totals ---
    const totalOreMinedVol = periodRows.reduce((s, r) => s + r.ore_mined_vol, 0);
    const totalOreMinedTonnes = periodRows.reduce((s, r) => s + r.ore_mined_tonnes, 0);
    const totalExPitCrusher = periodRows.reduce((s, r) => s + r.expit_to_crusher, 0);
    const totalExPitRompad = periodRows.reduce((s, r) => s + r.expit_to_rompad, 0);
    const totalRompadCrusher = periodRows.reduce((s, r) => s + r.rompad_to_crusher, 0);
    const totalMoved = totalExPitCrusher + totalExPitRompad;
    const totalToCrusher = totalExPitCrusher + totalRompadCrusher;
    const totalOreInPit = totalOreMinedTonnes - totalMoved;

    // --- Cumulative rompad inventory by GradeBin ---
    const cumInv = {};
    gradeBins.forEach(b => { cumInv[b] = 0; });

    const rompadInventory = sortedPeriods.map(k => {
      const row = { period: k };
      gradeBins.forEach(bin => {
        const inAmt = romInByBin[k]?.[bin] || 0;
        const outAmt = romOutByBin[k]?.[bin] || 0;
        cumInv[bin] += inAmt - outAmt;
        row[`inv_${bin}`] = Math.max(0, cumInv[bin]);
        row[`in_${bin}`] = inAmt;
        row[`out_${bin}`] = outAmt;
      });
      row.total_inventory = gradeBins.reduce((s, bin) => s + Math.max(0, cumInv[bin]), 0);
      return row;
    });

    // --- Current rompad snapshot ---
    const currentRompad = gradeBins.map(bin => ({
      name: bin, value: Math.max(0, cumInv[bin] || 0),
    })).filter(d => d.value > 0);
    const totalRompadInventory = currentRompad.reduce((s, d) => s + d.value, 0);

    // ═══════════════════════════════════════════════════════════════════
    // ── Block Reconciliation — cumulative + period breakdown ────────
    // Tracks per block: blasted this period vs previous, moved this
    // period vs previous, and running remaining balance.
    // ═══════════════════════════════════════════════════════════════════
    const isThisPeriod = (dateStr) => {
      if (selectedMonths.length === 0) return true; // no filter = everything is "this period"
      const d = parseDateField(dateStr);
      return d && selectedMonths.includes(getMonthKey(d));
    };
    const reconMap = {};
    const ensureReconBlock = (bid, prdId) => {
      if (!reconMap[bid]) reconMap[bid] = {
        BlockID: bid, PrdID: prdId || '—',
        // Blasted
        blasted_this_vol: 0, blasted_this_t: 0,
        blasted_prev_vol: 0, blasted_prev_t: 0,
        // Moved to crusher
        crush_this: 0, crush_prev: 0,
        // Moved to rompad
        rom_this: 0, rom_prev: 0,
        // Grade tracking (cumulative)
        grade_sum: 0, grade_wt: 0,
      };
    };

    // 1. ALL ore blasts (use enriched.act which is unfiltered by period)
    enriched.act.filter(r => r._type === 'Ore').forEach(r => {
      const bid = r.BlockID; if (!bid) return;
      ensureReconBlock(bid, r.PrdID);
      const vol = parseFloat(r.BlastedVolume) || 0;
      const t = volToTonnes(vol);
      if (isThisPeriod(r.Date)) {
        reconMap[bid].blasted_this_vol += vol;
        reconMap[bid].blasted_this_t += t;
      } else {
        reconMap[bid].blasted_prev_vol += vol;
        reconMap[bid].blasted_prev_t += t;
      }
    });

    // 2. ALL ExPitToCrusher movements
    exPitToCrusher.forEach(r => {
      const bid = r.BlockID; if (!bid) return;
      ensureReconBlock(bid, r.PrdID);
      const t = parseFloat(r.Tonnage) || 0;
      const g = parseFloat(r.Grade) || 0;
      if (isThisPeriod(r.Date)) { reconMap[bid].crush_this += t; }
      else { reconMap[bid].crush_prev += t; }
      reconMap[bid].grade_sum += g * t;
      reconMap[bid].grade_wt += t;
    });

    // 3. ALL ExPitToRompad movements
    exPitToRompad.forEach(r => {
      const bid = r.BlockID; if (!bid) return;
      ensureReconBlock(bid, r.PrdID);
      const t = parseFloat(r.Tonage) || parseFloat(r.Tonnage) || 0;
      const g = parseFloat(r.Grade) || 0;
      if (isThisPeriod(r.Date)) { reconMap[bid].rom_this += t; }
      else { reconMap[bid].rom_prev += t; }
      reconMap[bid].grade_sum += g * t;
      reconMap[bid].grade_wt += t;
    });

    // 4. Compute derived fields
    const blockReconAll = Object.values(reconMap).map(b => {
      const blasted_total_t = b.blasted_this_t + b.blasted_prev_t;
      const blasted_total_vol = b.blasted_this_vol + b.blasted_prev_vol;
      const moved_this = b.crush_this + b.rom_this;
      const moved_prev = b.crush_prev + b.rom_prev;
      const moved_total = moved_this + moved_prev;
      const remaining = Math.max(0, blasted_total_t - moved_total);
      const avg_grade = b.grade_wt > 0 ? b.grade_sum / b.grade_wt : 0;
      let status = 'In Pit';
      if (remaining <= 0 && moved_total > 0) status = 'Fully Moved';
      else if (moved_total > 0 && remaining > 0) status = 'Partial';
      return {
        ...b, blasted_total_t, blasted_total_vol, moved_this, moved_prev, moved_total,
        remaining, avg_grade, status,
      };
    });

    // Only show blocks with activity in the selected month(s).
    // "Activity" = blasted this period OR moved this period.
    // When no month filter is active, show all blocks.
    const blockRecon = (selectedMonths.length > 0
      ? blockReconAll.filter(b => b.blasted_this_t > 0 || b.moved_this > 0)
      : blockReconAll
    ).sort((a, b) => {
      const ord = { 'In Pit': 0, 'Partial': 1, 'Fully Moved': 2 };
      return (ord[a.status] || 0) - (ord[b.status] || 0) || a.BlockID.localeCompare(b.BlockID);
    });

    const inPitBlocks = blockRecon.filter(b => b.status === 'In Pit');
    const movedBlockList = blockRecon.filter(b => b.status !== 'In Pit');

    // 5. Summary totals for the reconciliation
    const reconSummary = {
      blasted_this: blockRecon.reduce((s, b) => s + b.blasted_this_t, 0),
      blasted_prev: blockRecon.reduce((s, b) => s + b.blasted_prev_t, 0),
      blasted_total: blockRecon.reduce((s, b) => s + b.blasted_total_t, 0),
      moved_this: blockRecon.reduce((s, b) => s + b.moved_this, 0),
      moved_prev: blockRecon.reduce((s, b) => s + b.moved_prev, 0),
      moved_total: blockRecon.reduce((s, b) => s + b.moved_total, 0),
      remaining: blockRecon.reduce((s, b) => s + b.remaining, 0),
    };

    return {
      periodRows, gradeBins, rompadInventory, currentRompad, blockRecon, reconSummary,
      totalOreMinedVol, totalOreMinedTonnes, totalExPitCrusher, totalExPitRompad,
      totalRompadCrusher, totalMoved, totalToCrusher, totalOreInPit,
      totalRompadInventory, inPitBlocks, movedBlockList,
    };
  }, [enriched, filtered, exPitToCrusher, exPitToRompad, rompadToCrusher, period, selectedMonths, passMonthFilter]);

  // ── Mining totals ──
  const totals = useMemo(() => {
    const t = {
      act_volume: 0, plan_volume: 0, act_drilled: 0, plan_drilled: 0,
      act_blasts: 0, plan_blasts: 0, act_ore_vol: 0, act_waste_vol: 0,
      plan_ore_vol: 0, plan_waste_vol: 0, act_explosives: 0, act_payable_vol: 0,
      act_tonnes: 0, plan_tonnes: 0, act_ore_tonnes: 0, act_waste_tonnes: 0,
      crush_act_tonnes: 0, crush_bgt_tonnes: 0, crush_act_content: 0, crush_bgt_content: 0,
    };
    periodData.forEach(d => {
      t.act_volume += d.act_volume; t.plan_volume += d.plan_volume;
      t.act_drilled += d.act_drilled; t.plan_drilled += d.plan_drilled;
      t.act_blasts += d.act_blasts; t.plan_blasts += d.plan_blasts;
      t.act_ore_vol += d.act_ore_vol; t.act_waste_vol += d.act_waste_vol;
      t.plan_ore_vol += d.plan_ore_vol; t.plan_waste_vol += d.plan_waste_vol;
      t.act_explosives += d.act_explosives; t.act_payable_vol += d.act_payable_vol;
      t.act_tonnes += d.act_tonnes; t.plan_tonnes += d.plan_tonnes;
      t.act_ore_tonnes += d.act_ore_tonnes; t.act_waste_tonnes += d.act_waste_tonnes;
    });
    crusherData.forEach(d => {
      t.crush_act_tonnes += d.act_tonnes; t.crush_bgt_tonnes += d.bgt_tonnes;
      t.crush_act_content += d.act_content; t.crush_bgt_content += d.bgt_content;
    });
    return t;
  }, [periodData, crusherData]);

  // ── Ore vs Waste pie ──
  const oreWastePie = useMemo(() => {
    return [{ name: 'Ore', value: totals.act_ore_vol }, { name: 'Waste', value: totals.act_waste_vol }].filter(d => d.value > 0);
  }, [totals]);

  // ── Block-level detail ──
  const blockDetail = useMemo(() => {
    const map = {};
    const ensureBlock = (key, prdId, blockId) => {
      if (!map[key]) map[key] = {
        PrdID: prdId, BlockID: blockId,
        act_vol: 0, act_drilled: 0, act_blasts: 0, act_ore_count: 0, act_waste_count: 0,
        plan_vol: 0, plan_drilled: 0, plan_blasts: 0, plan_grade: 0, plan_grade_wt: 0,
      };
    };
    filtered.act.forEach(r => {
      const key = `${r.PrdID || '—'}|${r.BlockID || '—'}`;
      ensureBlock(key, r.PrdID || '—', r.BlockID || '—');
      map[key].act_vol += parseFloat(r.BlastedVolume) || 0;
      map[key].act_drilled += parseFloat(r.DrilledMeters) || 0;
      map[key].act_blasts += 1;
      // Use BlastedMaterial to classify actual ore/waste
      if (r._type === 'Ore') map[key].act_ore_count += 1;
      else map[key].act_waste_count += 1;
    });
    filtered.plan.forEach(r => {
      const key = `${r.PrdID || '—'}|${r.BlockID || '—'}`;
      ensureBlock(key, r.PrdID || '—', r.BlockID || '—');
      const vol = parseFloat(r.BlastedVolume) || 0;
      const grade = parseFloat(r.BlastedGrade) || 0;
      map[key].plan_vol += vol; map[key].plan_drilled += parseFloat(r.DrilledMeters) || 0;
      map[key].plan_blasts += 1; map[key].plan_grade += grade * vol; map[key].plan_grade_wt += vol;
    });
    // Determine block type: for actuals use BlastedMaterial, for plan-only use grade
    return Object.values(map).map(b => {
      let blockType;
      if (b.act_blasts > 0) {
        // Actual data exists — use BlastedMaterial (majority wins)
        blockType = b.act_ore_count >= b.act_waste_count ? 'Ore' : 'Waste';
      } else {
        // Plan-only — use grade
        const avgGrade = b.plan_grade_wt > 0 ? b.plan_grade / b.plan_grade_wt : 0;
        blockType = avgGrade > 0 ? 'Ore' : 'Waste';
      }
      return { ...b, blockType };
    }).sort((a, b) => a.PrdID.localeCompare(b.PrdID) || a.BlockID.localeCompare(b.BlockID));
  }, [filtered]);

  // ═══════════════════════════════════════════════════════════════════
  // ── Compliance calculations ─────────────────────────────────────
  // ═══════════════════════════════════════════════════════════════════
  const compliance = useMemo(() => {
    const plannedMap = {};
    filtered.plan.forEach(r => {
      const key = `${r.PrdID || ''}|${r.BlockID || ''}`;
      if (!plannedMap[key]) plannedMap[key] = { PrdID: r.PrdID || '—', BlockID: r.BlockID || '—', plan_vol: 0, plan_drilled: 0, plan_grade: 0, plan_grade_wt: 0, plan_blasts: 0 };
      const vol = parseFloat(r.BlastedVolume) || 0;
      const grade = parseFloat(r.BlastedGrade) || 0;
      plannedMap[key].plan_vol += vol;
      plannedMap[key].plan_drilled += parseFloat(r.DrilledMeters) || 0;
      plannedMap[key].plan_blasts += 1;
      plannedMap[key].plan_grade += grade * vol;
      plannedMap[key].plan_grade_wt += vol;
    });

    const actualMap = {};
    filtered.act.forEach(r => {
      const key = `${r.PrdID || ''}|${r.BlockID || ''}`;
      if (!actualMap[key]) actualMap[key] = { PrdID: r.PrdID || '—', BlockID: r.BlockID || '—', act_vol: 0, act_drilled: 0, act_blasts: 0 };
      actualMap[key].act_vol += parseFloat(r.BlastedVolume) || 0;
      actualMap[key].act_drilled += parseFloat(r.DrilledMeters) || 0;
      actualMap[key].act_blasts += 1;
    });

    const plannedKeys = new Set(Object.keys(plannedMap));
    const actualKeys = new Set(Object.keys(actualMap));
    const onPlanKeys = [...plannedKeys].filter(k => actualKeys.has(k));
    const offPlanKeys = [...actualKeys].filter(k => !plannedKeys.has(k));
    const notMinedKeys = [...plannedKeys].filter(k => !actualKeys.has(k));

    const onPlan = onPlanKeys.map(k => ({ ...plannedMap[k], ...(actualMap[k] || {}), status: 'On Plan' }));
    const offPlan = offPlanKeys.map(k => ({ ...actualMap[k], plan_vol: 0, plan_drilled: 0, plan_blasts: 0, plan_grade: 0, plan_grade_wt: 0, status: 'Off Plan' }));
    const notMined = notMinedKeys.map(k => ({ ...plannedMap[k], act_vol: 0, act_drilled: 0, act_blasts: 0, status: 'Not Mined' }));

    const totalPlannedBlocks = plannedKeys.size;
    const onPlanCount = onPlanKeys.length;
    const offPlanCount = offPlanKeys.length;
    const notMinedCount = notMinedKeys.length;
    const totalActualBlocks = actualKeys.size;

    const planCompliancePct = totalPlannedBlocks > 0 ? (onPlanCount / totalPlannedBlocks * 100) : 0;
    const executionRatePct = totalPlannedBlocks > 0 ? (totalActualBlocks / totalPlannedBlocks * 100) : 0;
    const offPlanRatePct = totalActualBlocks > 0 ? (offPlanCount / totalActualBlocks * 100) : 0;

    const onPlanVolCompliance = onPlan.map(b => {
      const volVar = b.plan_vol > 0 ? ((b.act_vol - b.plan_vol) / b.plan_vol * 100) : 0;
      const drillVar = b.plan_drilled > 0 ? ((b.act_drilled - b.plan_drilled) / b.plan_drilled * 100) : 0;
      const avgGrade = b.plan_grade_wt > 0 ? b.plan_grade / b.plan_grade_wt : 0;
      return { ...b, volVar, drillVar, avgGrade, blockType: avgGrade > 0 ? 'Ore' : 'Waste' };
    });

    const totalOnPlanPlanVol = onPlan.reduce((s, b) => s + (b.plan_vol || 0), 0);
    const totalOnPlanActVol = onPlan.reduce((s, b) => s + (b.act_vol || 0), 0);
    const volCompliancePct = totalOnPlanPlanVol > 0 ? (totalOnPlanActVol / totalOnPlanPlanVol * 100) : 0;
    const totalOffPlanVol = offPlan.reduce((s, b) => s + (b.act_vol || 0), 0);
    const totalNotMinedVol = notMined.reduce((s, b) => s + (b.plan_vol || 0), 0);

    const periodCompMap = {};
    filtered.plan.forEach(r => {
      const key = r._period; if (!key) return;
      if (!periodCompMap[key]) periodCompMap[key] = { period: key, planned_blocks: new Set(), actual_blocks: new Set(), plan_vol: 0, act_vol: 0 };
      periodCompMap[key].planned_blocks.add(`${r.PrdID}|${r.BlockID}`);
      periodCompMap[key].plan_vol += parseFloat(r.BlastedVolume) || 0;
    });
    filtered.act.forEach(r => {
      const key = r._period; if (!key) return;
      if (!periodCompMap[key]) periodCompMap[key] = { period: key, planned_blocks: new Set(), actual_blocks: new Set(), plan_vol: 0, act_vol: 0 };
      periodCompMap[key].actual_blocks.add(`${r.PrdID}|${r.BlockID}`);
      periodCompMap[key].act_vol += parseFloat(r.BlastedVolume) || 0;
    });
    const periodCompliance = sortPeriodKeys(Object.keys(periodCompMap), period).map(k => {
      const d = periodCompMap[k];
      const planned = d.planned_blocks.size;
      const onP = [...d.planned_blocks].filter(b => d.actual_blocks.has(b)).length;
      const offP = [...d.actual_blocks].filter(b => !d.planned_blocks.has(b)).length;
      const notM = planned - onP;
      return {
        period: k, planned_blocks: planned, on_plan: onP, off_plan: offP, not_mined: notM,
        compliance_pct: planned > 0 ? (onP / planned * 100) : 0,
        plan_vol: d.plan_vol, act_vol: d.act_vol,
        vol_compliance_pct: d.plan_vol > 0 ? (d.act_vol / d.plan_vol * 100) : 0,
      };
    });

    return {
      onPlan, offPlan, notMined, onPlanVolCompliance,
      onPlanCount, offPlanCount, notMinedCount,
      totalPlannedBlocks, totalActualBlocks,
      planCompliancePct, executionRatePct, offPlanRatePct, volCompliancePct,
      totalOnPlanPlanVol, totalOnPlanActVol, totalOffPlanVol, totalNotMinedVol,
      periodCompliance,
    };
  }, [filtered, period]);

  // ── Crusher totals ──
  const crusherTotals = useMemo(() => {
    const t = {
      act_tonnes: 0, bgt_tonnes: 0, act_content: 0, bgt_content: 0,
      act_grade_sum: 0, act_grade_wt: 0, bgt_grade_sum: 0, bgt_grade_wt: 0,
      direct_tonnes: 0, direct_grade_sum: 0, direct_grade_wt: 0, direct_content: 0,
      rompad_tonnes: 0, rompad_grade_sum: 0, rompad_grade_wt: 0, rompad_content: 0,
    };
    crusherData.forEach(d => {
      t.act_tonnes += d.act_tonnes; t.bgt_tonnes += d.bgt_tonnes;
      t.act_content += d.act_content; t.bgt_content += d.bgt_content;
      t.act_grade_sum += d.act_grade_sum; t.act_grade_wt += d.act_grade_wt;
      t.bgt_grade_sum += d.bgt_grade_sum; t.bgt_grade_wt += d.bgt_grade_wt;
      t.direct_tonnes += d.direct_tonnes; t.direct_grade_sum += d.direct_grade_sum;
      t.direct_grade_wt += d.direct_grade_wt; t.direct_content += d.direct_content;
      t.rompad_tonnes += d.rompad_tonnes; t.rompad_grade_sum += d.rompad_grade_sum;
      t.rompad_grade_wt += d.rompad_grade_wt; t.rompad_content += d.rompad_content;
    });
    t.act_avg_grade = t.act_grade_wt > 0 ? t.act_grade_sum / t.act_grade_wt : 0;
    t.bgt_avg_grade = t.bgt_grade_wt > 0 ? t.bgt_grade_sum / t.bgt_grade_wt : 0;
    t.direct_avg_grade = t.direct_grade_wt > 0 ? t.direct_grade_sum / t.direct_grade_wt : 0;
    t.rompad_avg_grade = t.rompad_grade_wt > 0 ? t.rompad_grade_sum / t.rompad_grade_wt : 0;
    return t;
  }, [crusherData]);

  // ── Toggle helpers ──
  const toggleItem = useCallback((setter) => (item) => {
    setter(prev => prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]);
  }, []);

  // ── Loading / Error ──
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-100">
        <div className="flex flex-col items-center space-y-3">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-yellow-500 border-t-transparent" />
          <p className="text-gray-500 text-sm">Loading statistics...</p>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-100 p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-w-md text-center">
          <p className="text-red-600 text-sm font-medium">{error}</p>
          <button onClick={() => window.location.reload()} className="mt-2 px-4 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full bg-gray-100 overflow-hidden">
      {/* ── Header + Sub-tabs ── */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-200 shadow-sm">
        <div className="px-3 py-1.5">
          <h2 className="text-xs sm:text-sm font-bold text-gray-800 uppercase tracking-wide">Eureka Open Pit Statistics</h2>
          <p className="text-[9px] text-gray-400">SG = {SG} t/m³</p>
        </div>
        <div className="flex border-t border-gray-100">
          {SUB_TABS.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium transition-all ${
                activeTab === tab.key ? 'bg-yellow-500 text-white' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
              }`}>
              <span>{tab.icon}</span><span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Shared Filters ── */}
      <div className="bg-white border-b border-gray-200 px-3 py-2">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <div>
            <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Group By</label>
            <select value={period} onChange={e => setPeriod(e.target.value)}
              className="w-full p-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-yellow-500">
              <option value="Day">Day</option>
              <option value="Month">Month</option>
              <option value="Quarter">Quarter</option>
              <option value="Year">Year</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Filter Month</label>
            <MultiSelectDropdown label={selectedMonths.length ? `${selectedMonths.length} selected` : 'All'}
              list={filterOptions.months} selectedlist={selectedMonths} itemClick={toggleItem(setSelectedMonths)} />
          </div>
          <div>
            <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Block ID</label>
            <MultiSelectDropdown label={selectedBlocks.length ? `${selectedBlocks.length} selected` : 'All'}
              list={filterOptions.blocks} selectedlist={selectedBlocks} itemClick={toggleItem(setSelectedBlocks)} />
          </div>
          <div>
            <label className="block text-[10px] font-medium text-gray-500 mb-0.5">Period ID</label>
            <MultiSelectDropdown label={selectedPrdIds.length ? `${selectedPrdIds.length} selected` : 'All'}
              list={filterOptions.prdIds} selectedlist={selectedPrdIds} itemClick={toggleItem(setSelectedPrdIds)} />
          </div>
          <div className="flex items-end">
            <button onClick={() => { setSelectedMonths([]); setSelectedBlocks([]); setSelectedPrdIds([]); }}
              className="w-full p-1 border border-gray-300 rounded text-xs text-gray-600 hover:bg-gray-100 transition-colors">
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* ── Tab Content ── */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'mining' && (
          <MiningTab periodData={periodData} totals={totals} oreWastePie={oreWastePie} blockDetail={blockDetail} period={period} />
        )}
        {activeTab === 'compliance' && (
          <ComplianceTab compliance={compliance} period={period} />
        )}
        {activeTab === 'oreMovement' && (
          <OreMovementTab data={oreMovement} period={period} />
        )}
        {activeTab === 'processing' && (
          <ProcessingTab crusherData={crusherData} crusherTotals={crusherTotals} period={period} />
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ── MINING TAB ────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════
function MiningTab({ periodData, totals, oreWastePie, blockDetail, period }) {
  const [blockFilter, setBlockFilter] = useState('All');

  const filteredBlocks = useMemo(() => {
    if (blockFilter === 'All') return blockDetail;
    return blockDetail.filter(row => row.blockType === blockFilter);
  }, [blockDetail, blockFilter]);

  return (
    <div className="p-2 sm:p-3 space-y-3">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <KpiCard title="Blast Count" actual={fmt(totals.act_blasts)} plan={fmt(totals.plan_blasts)} variance={pct(totals.act_blasts, totals.plan_blasts)} />
        <KpiCard title="Blasted Volume" actual={`${fmt(totals.act_volume)} m³`} plan={`${fmt(totals.plan_volume)} m³`} variance={pct(totals.act_volume, totals.plan_volume)} />
        <KpiCard title="Blasted Tonnes" actual={`${fmt(totals.act_tonnes)} t`} plan={`${fmt(totals.plan_tonnes)} t`} variance={pct(totals.act_tonnes, totals.plan_tonnes)} />
        <KpiCard title="Drilled Meters" actual={`${fmt(totals.act_drilled)} m`} plan={`${fmt(totals.plan_drilled)} m`} variance={pct(totals.act_drilled, totals.plan_drilled)} />
        <KpiCard title="Ore Tonnes" actual={`${fmt(totals.act_ore_tonnes)} t`} plan="—" />
        <KpiCard title="Explosives" actual={`${fmt(totals.act_explosives)} kg`} plan="—" />
      </div>

      {/* Summary Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-xs font-semibold text-gray-700 p-3 border-b border-gray-200 uppercase">Summary — Actual vs Planned (SG = {SG})</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50"><tr>
              <th className="px-2 py-1.5 text-left font-medium text-gray-500">Metric</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">Actual</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">Planned</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">Variance</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">%</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-100">
              <SummaryRow label="Blast Count" actual={totals.act_blasts} plan={totals.plan_blasts} unit="" />
              <SummaryRow label="Blasted Volume" actual={totals.act_volume} plan={totals.plan_volume} unit="m³" />
              <SummaryRow label="Blasted Tonnes" actual={totals.act_tonnes} plan={totals.plan_tonnes} unit="t" />
              <SummaryRow label="Drilled Meters" actual={totals.act_drilled} plan={totals.plan_drilled} unit="m" />
              <SummaryRow label="Ore Volume" actual={totals.act_ore_vol} plan={totals.plan_ore_vol} unit="m³" />
              <SummaryRow label="Ore Tonnes" actual={totals.act_ore_tonnes} plan={volToTonnes(totals.plan_ore_vol)} unit="t" />
              <SummaryRow label="Waste Volume" actual={totals.act_waste_vol} plan={totals.plan_waste_vol} unit="m³" />
            </tbody>
          </table>
        </div>
      </div>

      {/* Blasted Volume chart */}
      <ChartCard title="Blasted Volume — Actual vs Planned (m³)">
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={periodData} margin={{ top: 10, right: 10, bottom: 20, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="period" tick={{ fontSize: 10, fill: '#6b7280' }} angle={-30} textAnchor="end" height={60} />
            <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} tickFormatter={v => v.toLocaleString()} />
            <Tooltip content={<CustomTooltip unit="m³" />} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="act_volume" name="Actual" fill={COLORS.actual} radius={[2,2,0,0]} />
            <Bar dataKey="plan_volume" name="Planned" fill={COLORS.planned} radius={[2,2,0,0]} />
            <Line dataKey="vol_variance_pct" name="Variance %" stroke={COLORS.line} yAxisId="right" dot={{ r: 3 }} strokeWidth={2} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: '#6b7280' }} tickFormatter={v => `${v}%`} />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Drilled Meters chart */}
      <ChartCard title="Drilled Meters — Actual vs Planned (m)">
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={periodData} margin={{ top: 10, right: 10, bottom: 20, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="period" tick={{ fontSize: 10, fill: '#6b7280' }} angle={-30} textAnchor="end" height={60} />
            <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} tickFormatter={v => v.toLocaleString()} />
            <Tooltip content={<CustomTooltip unit="m" />} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="act_drilled" name="Actual" fill={COLORS.actual} radius={[2,2,0,0]} />
            <Bar dataKey="plan_drilled" name="Planned" fill={COLORS.planned} radius={[2,2,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Blast Count chart */}
      <ChartCard title="Blast Count — Actual vs Planned">
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={periodData} margin={{ top: 10, right: 10, bottom: 20, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="period" tick={{ fontSize: 10, fill: '#6b7280' }} angle={-30} textAnchor="end" height={60} />
            <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} />
            <Tooltip content={<CustomTooltip unit="" />} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="act_blasts" name="Actual" fill={COLORS.actual} radius={[2,2,0,0]} />
            <Bar dataKey="plan_blasts" name="Planned" fill={COLORS.planned} radius={[2,2,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Ore vs Waste */}
      <ChartCard title="Ore vs Waste — Actual Blasted Volume (m³)">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={periodData} margin={{ top: 10, right: 10, bottom: 20, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="period" tick={{ fontSize: 10, fill: '#6b7280' }} angle={-30} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} tickFormatter={v => v.toLocaleString()} />
                <Tooltip content={<CustomTooltip unit="m³" />} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="act_ore_vol" name="Ore" stackId="a" fill={COLORS.ore} />
                <Bar dataKey="act_waste_vol" name="Waste" stackId="a" fill={COLORS.waste} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          {oreWastePie.length > 0 && (
            <div className="w-full lg:w-48 flex items-center justify-center">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart><Pie data={oreWastePie} dataKey="value" nameKey="name" cx="50%" cy="50%"
                  outerRadius={70} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={false} fontSize={10}>
                  {oreWastePie.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                </Pie><Tooltip formatter={v => fmt(v)} /></PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </ChartCard>

      {/* Powder Factor */}
      <ChartCard title="Powder Factor — Actual vs Planned (kg/m³)">
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={periodData.filter(d => d.pf_count > 0)} margin={{ top: 10, right: 10, bottom: 20, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="period" tick={{ fontSize: 10, fill: '#6b7280' }} angle={-30} textAnchor="end" height={60} />
            <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} tickFormatter={v => v.toFixed(2)} />
            <Tooltip content={<CustomTooltip unit="kg/m³" dec={3} />} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="act_avg_pf" name="Actual PF" fill={COLORS.actual} radius={[2,2,0,0]} />
            <Bar dataKey="plan_avg_pf" name="Planned PF" fill={COLORS.planned} radius={[2,2,0,0]} />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Block detail table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between p-3 border-b border-gray-200">
          <h3 className="text-xs font-semibold text-gray-700 uppercase">Block-Level Detail</h3>
          <div className="flex items-center gap-1">
            {['All', 'Ore', 'Waste'].map(opt => (
              <button key={opt} onClick={() => setBlockFilter(opt)}
                className={`px-2.5 py-1 rounded text-[10px] font-semibold transition-all ${
                  blockFilter === opt
                    ? opt === 'Ore' ? 'bg-sky-500 text-white' : opt === 'Waste' ? 'bg-stone-500 text-white' : 'bg-yellow-500 text-white'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}>
                {opt} ({opt === 'All' ? blockDetail.length : blockDetail.filter(r => r.blockType === opt).length})
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto max-h-80">
          <table className="min-w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50 sticky top-0"><tr>
              <th className="px-2 py-1.5 text-left font-medium text-gray-500">Prd ID</th>
              <th className="px-2 py-1.5 text-left font-medium text-gray-500">Block</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">Type</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">Act Vol</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">Plan Vol</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">Var %</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">Act Drill</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">Plan Drill</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">Grade</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-100">
              {filteredBlocks.map((row, i) => {
                const avgGrade = row.plan_grade_wt > 0 ? row.plan_grade / row.plan_grade_wt : 0;
                const volVar = row.plan_vol > 0 ? ((row.act_vol - row.plan_vol) / row.plan_vol * 100) : null;
                return (
                  <tr key={i} className={`hover:bg-yellow-50 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                    <td className="px-2 py-1 font-medium text-gray-800">{row.PrdID}</td>
                    <td className="px-2 py-1 text-gray-700">{row.BlockID}</td>
                    <td className="px-2 py-1 text-center"><TypeBadge type={row.blockType} /></td>
                    <td className="px-2 py-1 text-center text-gray-700">{fmt(row.act_vol)}</td>
                    <td className="px-2 py-1 text-center text-gray-700">{fmt(row.plan_vol)}</td>
                    <td className="px-2 py-1 text-center">{volVar !== null ? <VarianceBadge value={volVar} /> : '—'}</td>
                    <td className="px-2 py-1 text-center text-gray-700">{fmt(row.act_drilled)}</td>
                    <td className="px-2 py-1 text-center text-gray-700">{fmt(row.plan_drilled)}</td>
                    <td className="px-2 py-1 text-center text-gray-700">{avgGrade > 0 ? avgGrade.toFixed(2) : '—'}</td>
                  </tr>
                );
              })}
              {filteredBlocks.length === 0 && <tr><td colSpan={9} className="px-4 py-6 text-center text-gray-400">No {blockFilter !== 'All' ? blockFilter.toLowerCase() + ' ' : ''}blocks</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ── COMPLIANCE TAB ────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════
function ComplianceTab({ compliance: c, period }) {
  return (
    <div className="p-2 sm:p-3 space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <ComplianceCard title="Plan Compliance" value={`${c.planCompliancePct.toFixed(1)}%`}
          sub={`${c.onPlanCount} of ${c.totalPlannedBlocks} planned blocks mined`}
          color={c.planCompliancePct >= 80 ? 'indigo' : c.planCompliancePct >= 60 ? 'amber' : 'stone'} />
        <ComplianceCard title="On Plan" value={c.onPlanCount} sub="Planned & mined" color="indigo" />
        <ComplianceCard title="Off Plan" value={c.offPlanCount} sub="Mined but NOT planned" color="amber" />
        <ComplianceCard title="Not Mined" value={c.notMinedCount} sub="Planned but NOT mined" color="stone" />
        <ComplianceCard title="Volume Compliance" value={`${c.volCompliancePct.toFixed(1)}%`}
          sub={`${fmt(c.totalOnPlanActVol)} / ${fmt(c.totalOnPlanPlanVol)} m³`}
          color={c.volCompliancePct >= 90 ? 'indigo' : c.volCompliancePct >= 70 ? 'amber' : 'stone'} />
        <ComplianceCard title="Off-Plan Rate" value={`${c.offPlanRatePct.toFixed(1)}%`}
          sub={`${c.offPlanCount} of ${c.totalActualBlocks} mined blocks`}
          color={c.offPlanRatePct <= 10 ? 'indigo' : c.offPlanRatePct <= 25 ? 'amber' : 'stone'} />
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-xs font-semibold text-gray-700 p-3 border-b border-gray-200 uppercase">Compliance Summary</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50"><tr>
              <th className="px-2 py-1.5 text-left font-medium text-gray-500">Metric</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">Value</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-100">
              <tr className="hover:bg-gray-50"><td className="px-2 py-1.5 font-medium text-gray-800">Total Planned Blocks</td><td className="px-2 py-1.5 text-center text-gray-700">{c.totalPlannedBlocks}</td></tr>
              <tr className="hover:bg-gray-50"><td className="px-2 py-1.5 font-medium text-gray-800">Total Mined Blocks</td><td className="px-2 py-1.5 text-center text-gray-700">{c.totalActualBlocks}</td></tr>
              <tr className="hover:bg-gray-50"><td className="px-2 py-1.5 font-medium text-gray-800">Blocks Mined On Plan</td><td className="px-2 py-1.5 text-center text-indigo-700 font-semibold">{c.onPlanCount}</td></tr>
              <tr className="hover:bg-gray-50"><td className="px-2 py-1.5 font-medium text-gray-800">Blocks Mined Off Plan</td><td className="px-2 py-1.5 text-center text-amber-700 font-semibold">{c.offPlanCount}</td></tr>
              <tr className="hover:bg-gray-50"><td className="px-2 py-1.5 font-medium text-gray-800">Planned Blocks Not Mined</td><td className="px-2 py-1.5 text-center text-stone-600 font-semibold">{c.notMinedCount}</td></tr>
              <tr className="hover:bg-gray-50"><td className="px-2 py-1.5 font-medium text-gray-800">Execution Rate</td><td className="px-2 py-1.5 text-center text-gray-700">{c.executionRatePct.toFixed(1)}%</td></tr>
              <tr className="hover:bg-gray-50"><td className="px-2 py-1.5 font-medium text-gray-800">Plan Compliance</td><td className="px-2 py-1.5 text-center font-semibold" style={{ color: c.planCompliancePct >= 80 ? '#4338ca' : c.planCompliancePct >= 60 ? '#b45309' : '#78716c' }}>{c.planCompliancePct.toFixed(1)}%</td></tr>
              <tr className="hover:bg-gray-50"><td className="px-2 py-1.5 font-medium text-gray-800">Volume Compliance (On-Plan)</td><td className="px-2 py-1.5 text-center text-gray-700">{c.volCompliancePct.toFixed(1)}%</td></tr>
              <tr className="hover:bg-gray-50"><td className="px-2 py-1.5 font-medium text-gray-800">Off-Plan Volume</td><td className="px-2 py-1.5 text-center text-amber-700">{fmt(c.totalOffPlanVol)} m³</td></tr>
              <tr className="hover:bg-gray-50"><td className="px-2 py-1.5 font-medium text-gray-800">Not Mined Volume (Lost)</td><td className="px-2 py-1.5 text-center text-stone-600">{fmt(c.totalNotMinedVol)} m³</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <ChartCard title="Block Compliance Breakdown">
        <div className="flex items-center justify-center">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={[
                { name: 'On Plan', value: c.onPlanCount },
                { name: 'Off Plan', value: c.offPlanCount },
                { name: 'Not Mined', value: c.notMinedCount },
              ].filter(d => d.value > 0)} dataKey="value" nameKey="name" cx="50%" cy="50%"
                outerRadius={90} label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                labelLine={true} fontSize={10}>
                <Cell fill={COLORS.onPlan} />
                <Cell fill={COLORS.offPlan} />
                <Cell fill={COLORS.notMined} />
              </Pie>
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </ChartCard>

      <ChartCard title={`Plan Compliance by ${period}`}>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={c.periodCompliance} margin={{ top: 10, right: 10, bottom: 20, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="period" tick={{ fontSize: 10, fill: '#6b7280' }} angle={-30} textAnchor="end" height={60} />
            <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} />
            <Tooltip content={<ComplianceTooltip />} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="on_plan" name="On Plan" stackId="a" fill={COLORS.onPlan} />
            <Bar dataKey="off_plan" name="Off Plan" stackId="a" fill={COLORS.offPlan} />
            <Bar dataKey="not_mined" name="Not Mined" stackId="a" fill={COLORS.notMined} />
            <Line dataKey="compliance_pct" name="Compliance %" stroke={COLORS.line} yAxisId="right" dot={{ r: 3 }} strokeWidth={2} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: '#6b7280' }} tickFormatter={v => `${v.toFixed(0)}%`} domain={[0, 100]} />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title={`Volume Compliance by ${period} — Actual vs Plan (m³)`}>
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={c.periodCompliance} margin={{ top: 10, right: 10, bottom: 20, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="period" tick={{ fontSize: 10, fill: '#6b7280' }} angle={-30} textAnchor="end" height={60} />
            <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} tickFormatter={v => v.toLocaleString()} />
            <Tooltip content={<CustomTooltip unit="m³" />} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="act_vol" name="Actual Volume" fill={COLORS.actual} radius={[2,2,0,0]} />
            <Bar dataKey="plan_vol" name="Planned Volume" fill={COLORS.planned} radius={[2,2,0,0]} />
            <Line dataKey="vol_compliance_pct" name="Vol Compliance %" stroke={COLORS.line} yAxisId="right" dot={{ r: 3 }} strokeWidth={2} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: '#6b7280' }} tickFormatter={v => `${v.toFixed(0)}%`} />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-xs font-semibold text-gray-700 p-3 border-b border-gray-200 uppercase">On-Plan Blocks — Volume Compliance</h3>
        <div className="overflow-x-auto max-h-72">
          <table className="min-w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50 sticky top-0"><tr>
              <th className="px-2 py-1.5 text-left font-medium text-gray-500">Prd ID</th>
              <th className="px-2 py-1.5 text-left font-medium text-gray-500">Block</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">Type</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">Act Vol</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">Plan Vol</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">Vol Var %</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">Act Drill</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">Plan Drill</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">Drill Var %</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-100">
              {c.onPlanVolCompliance.map((row, i) => (
                <tr key={i} className={`hover:bg-yellow-50 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                  <td className="px-2 py-1 font-medium text-gray-800">{row.PrdID}</td>
                  <td className="px-2 py-1 text-gray-700">{row.BlockID}</td>
                  <td className="px-2 py-1 text-center"><TypeBadge type={row.blockType} /></td>
                  <td className="px-2 py-1 text-center text-gray-700">{fmt(row.act_vol)}</td>
                  <td className="px-2 py-1 text-center text-gray-700">{fmt(row.plan_vol)}</td>
                  <td className="px-2 py-1 text-center"><VarianceBadge value={row.volVar} /></td>
                  <td className="px-2 py-1 text-center text-gray-700">{fmt(row.act_drilled)}</td>
                  <td className="px-2 py-1 text-center text-gray-700">{fmt(row.plan_drilled)}</td>
                  <td className="px-2 py-1 text-center"><VarianceBadge value={row.drillVar} /></td>
                </tr>
              ))}
              {c.onPlanVolCompliance.length === 0 && <tr><td colSpan={9} className="px-4 py-6 text-center text-gray-400">No on-plan blocks</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {c.offPlan.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-amber-300">
          <h3 className="text-xs font-semibold text-amber-800 p-3 border-b border-amber-300 uppercase">Off-Plan Blocks — Mined Without Plan</h3>
          <div className="overflow-x-auto max-h-60">
            <table className="min-w-full divide-y divide-gray-200 text-xs">
              <thead className="bg-amber-50 sticky top-0"><tr>
                <th className="px-2 py-1.5 text-left font-medium text-gray-500">Prd ID</th>
                <th className="px-2 py-1.5 text-left font-medium text-gray-500">Block</th>
                <th className="px-2 py-1.5 text-center font-medium text-gray-500">Actual Vol (m³)</th>
                <th className="px-2 py-1.5 text-center font-medium text-gray-500">Actual Drilled (m)</th>
                <th className="px-2 py-1.5 text-center font-medium text-gray-500">Blasts</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {c.offPlan.map((row, i) => (
                  <tr key={i} className={`hover:bg-amber-50 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                    <td className="px-2 py-1 font-medium text-gray-800">{row.PrdID}</td>
                    <td className="px-2 py-1 text-gray-700">{row.BlockID}</td>
                    <td className="px-2 py-1 text-center text-gray-700">{fmt(row.act_vol)}</td>
                    <td className="px-2 py-1 text-center text-gray-700">{fmt(row.act_drilled)}</td>
                    <td className="px-2 py-1 text-center text-gray-700">{row.act_blasts}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {c.notMined.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-stone-300">
          <h3 className="text-xs font-semibold text-stone-700 p-3 border-b border-stone-300 uppercase">Planned Blocks Not Mined</h3>
          <div className="overflow-x-auto max-h-60">
            <table className="min-w-full divide-y divide-gray-200 text-xs">
              <thead className="bg-stone-50 sticky top-0"><tr>
                <th className="px-2 py-1.5 text-left font-medium text-gray-500">Prd ID</th>
                <th className="px-2 py-1.5 text-left font-medium text-gray-500">Block</th>
                <th className="px-2 py-1.5 text-center font-medium text-gray-500">Type</th>
                <th className="px-2 py-1.5 text-center font-medium text-gray-500">Planned Vol (m³)</th>
                <th className="px-2 py-1.5 text-center font-medium text-gray-500">Planned Drill (m)</th>
                <th className="px-2 py-1.5 text-center font-medium text-gray-500">Planned Grade</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {c.notMined.map((row, i) => {
                  const avgGrade = row.plan_grade_wt > 0 ? row.plan_grade / row.plan_grade_wt : 0;
                  return (
                    <tr key={i} className={`hover:bg-stone-50 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                      <td className="px-2 py-1 font-medium text-gray-800">{row.PrdID}</td>
                      <td className="px-2 py-1 text-gray-700">{row.BlockID}</td>
                      <td className="px-2 py-1 text-center"><TypeBadge type={avgGrade > 0 ? 'Ore' : 'Waste'} /></td>
                      <td className="px-2 py-1 text-center text-gray-700">{fmt(row.plan_vol)}</td>
                      <td className="px-2 py-1 text-center text-gray-700">{fmt(row.plan_drilled)}</td>
                      <td className="px-2 py-1 text-center text-gray-700">{avgGrade > 0 ? avgGrade.toFixed(2) + ' g/t' : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ── ORE MOVEMENT TAB ─────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════
function OreMovementTab({ data: d, period }) {
  const directPct = d.totalMoved > 0 ? (d.totalExPitCrusher / d.totalMoved * 100) : 0;
  const rompadPct = d.totalMoved > 0 ? (d.totalExPitRompad / d.totalMoved * 100) : 0;

  return (
    <div className="p-2 sm:p-3 space-y-3">

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <ComplianceCard title="Ore Mined" value={`${fmt(d.totalOreMinedTonnes)} t`}
          sub={`${fmt(d.totalOreMinedVol)} m³ (SG ${SG})`} color="indigo" />
        <ComplianceCard title="Direct to Crusher" value={`${fmt(d.totalExPitCrusher)} t`}
          sub={`${directPct.toFixed(0)}% of moved ore`} color="indigo" />
        <ComplianceCard title="To ROM Pad" value={`${fmt(d.totalExPitRompad)} t`}
          sub={`${rompadPct.toFixed(0)}% of moved ore`} color="amber" />
        <ComplianceCard title="ROM to Crusher" value={`${fmt(d.totalRompadCrusher)} t`}
          sub="From stockpile to crusher" color="indigo" />
        <ComplianceCard title="Ore in Pit" value={`${fmt(Math.max(0, d.totalOreInPit))} t`}
          sub={`${d.inPitBlocks.length} blocks not moved`}
          color={d.totalOreInPit > 0 ? 'amber' : 'stone'} />
        <ComplianceCard title="ROM Pad Stock" value={`${fmt(d.totalRompadInventory)} t`}
          sub={`${d.currentRompad.length} grade bins`} color="stone" />
      </div>

      {/* ── Flow Diagram ── */}
      <OreFlowDiagram data={d} />

      {/* ── Ore Mined vs Moved (tonnes) by period ── */}
      <ChartCard title={`Ore Tonnage Reconciliation by ${period} — Mined vs Moved (t)`}>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={d.periodRows} margin={{ top: 10, right: 10, bottom: 20, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="period" tick={{ fontSize: 10, fill: '#6b7280' }} angle={-30} textAnchor="end" height={60} />
            <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} tickFormatter={v => v.toLocaleString()} />
            <Tooltip content={<CustomTooltip unit="t" />} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="ore_mined_tonnes" name="Ore Mined (SG converted)" fill={COLORS.ore} radius={[2,2,0,0]} />
            <Bar dataKey="total_moved" name="Total Moved Out" fill={COLORS.directCrusher} radius={[2,2,0,0]} />
            <Line dataKey="ore_in_pit" name="Ore in Pit (balance)" stroke={COLORS.oreInPit} dot={{ r: 3 }} strokeWidth={2} />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* ── Material Movement Breakdown by period ── */}
      <ChartCard title={`Ore Movement Destinations by ${period} (t)`}>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={d.periodRows} margin={{ top: 10, right: 10, bottom: 20, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="period" tick={{ fontSize: 10, fill: '#6b7280' }} angle={-30} textAnchor="end" height={60} />
            <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} tickFormatter={v => v.toLocaleString()} />
            <Tooltip content={<CustomTooltip unit="t" />} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="expit_to_crusher" name="Direct to Crusher" stackId="a" fill={COLORS.directCrusher} />
            <Bar dataKey="expit_to_rompad" name="To ROM Pad" stackId="a" fill={COLORS.toRompad} />
            <Bar dataKey="rompad_to_crusher" name="ROM to Crusher" fill={COLORS.fromRompad} radius={[2,2,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* ── Crusher Feed Composition ── */}
      <ChartCard title={`Total Crusher Feed by ${period} — Direct vs ROM Pad (t)`}>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={d.periodRows} margin={{ top: 10, right: 10, bottom: 20, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="period" tick={{ fontSize: 10, fill: '#6b7280' }} angle={-30} textAnchor="end" height={60} />
            <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} tickFormatter={v => v.toLocaleString()} />
            <Tooltip content={<CustomTooltip unit="t" />} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="expit_to_crusher" name="Direct from Pit" stackId="a" fill={COLORS.directCrusher} />
            <Bar dataKey="rompad_to_crusher" name="From ROM Pad" stackId="a" fill={COLORS.fromRompad} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* ── ROM Pad Cumulative Inventory by GradeBin (stacked area) ── */}
      {d.gradeBins.length > 0 && (
        <ChartCard title={`ROM Pad Cumulative Inventory by Grade Bin (t)`}>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={d.rompadInventory} margin={{ top: 10, right: 10, bottom: 20, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="period" tick={{ fontSize: 10, fill: '#6b7280' }} angle={-30} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} tickFormatter={v => v.toLocaleString()} />
              <Tooltip content={<CustomTooltip unit="t" />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {d.gradeBins.map((bin, i) => (
                <Area key={bin} type="monotone" dataKey={`inv_${bin}`} name={bin} stackId="1"
                  fill={GRADE_BIN_COLORS[i % GRADE_BIN_COLORS.length]}
                  stroke={GRADE_BIN_COLORS[i % GRADE_BIN_COLORS.length]} fillOpacity={0.7} />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {/* ── Current ROM Pad Breakdown (pie) ── */}
      {d.currentRompad.length > 0 && (
        <ChartCard title="Current ROM Pad Stockpile by Grade Bin">
          <div className="flex flex-col lg:flex-row items-center gap-4">
            <div className="flex-1 w-full">
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={d.currentRompad} dataKey="value" nameKey="name" cx="50%" cy="50%"
                    outerRadius={90} label={({ name, value, percent }) => `${name}: ${fmt(value)}t (${(percent * 100).toFixed(0)}%)`}
                    labelLine={true} fontSize={10}>
                    {d.currentRompad.map((_, i) => <Cell key={i} fill={GRADE_BIN_COLORS[i % GRADE_BIN_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={v => `${fmt(v)} t`} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="w-full lg:w-56">
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-3">
                <h4 className="text-[10px] font-semibold text-gray-500 uppercase mb-2">Stockpile Summary</h4>
                {d.currentRompad.map((bin, i) => (
                  <div key={i} className="flex justify-between items-center py-0.5 text-xs">
                    <div className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: GRADE_BIN_COLORS[i % GRADE_BIN_COLORS.length] }} />
                      <span className="text-gray-700 font-medium">{bin.name}</span>
                    </div>
                    <span className="text-gray-900 font-semibold">{fmt(bin.value)} t</span>
                  </div>
                ))}
                <div className="border-t border-gray-300 mt-1 pt-1 flex justify-between text-xs font-bold">
                  <span className="text-gray-700">Total</span>
                  <span className="text-gray-900">{fmt(d.totalRompadInventory)} t</span>
                </div>
              </div>
            </div>
          </div>
        </ChartCard>
      )}

      {/* ── Period Detail Table ── */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-xs font-semibold text-gray-700 p-3 border-b border-gray-200 uppercase">Ore Movement — Period Detail (t)</h3>
        <div className="overflow-x-auto max-h-80">
          <table className="min-w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50 sticky top-0"><tr>
              <th className="px-2 py-1.5 text-left font-medium text-gray-500">Period</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">Mined (t)</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">Direct Crush</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">To ROM</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">ROM→Crush</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">Total Crush</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">Ore in Pit</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-100">
              {d.periodRows.map((row, i) => (
                <tr key={i} className={`hover:bg-yellow-50 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                  <td className="px-2 py-1 font-medium text-gray-800">{row.period}</td>
                  <td className="px-2 py-1 text-center text-gray-700">{fmt(row.ore_mined_tonnes)}</td>
                  <td className="px-2 py-1 text-center text-cyan-700">{fmt(row.expit_to_crusher)}</td>
                  <td className="px-2 py-1 text-center text-emerald-700">{fmt(row.expit_to_rompad)}</td>
                  <td className="px-2 py-1 text-center text-violet-700">{fmt(row.rompad_to_crusher)}</td>
                  <td className="px-2 py-1 text-center font-semibold text-gray-800">{fmt(row.total_to_crusher)}</td>
                  <td className="px-2 py-1 text-center">
                    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                      row.ore_in_pit > 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'
                    }`}>{fmt(Math.max(0, row.ore_in_pit))}</span>
                  </td>
                </tr>
              ))}
              {d.periodRows.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-400">No data</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Block Reconciliation Summary ── */}
      {d.blockRecon.length > 0 && (
        <>
          {/* Reconciliation Summary Cards */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
            <h3 className="text-xs font-semibold text-gray-700 border-b border-gray-200 pb-2 mb-3 uppercase">
              Ore Block Reconciliation Summary
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 text-xs">
              <div className="bg-sky-50 border border-sky-200 rounded-lg p-2 text-center">
                <p className="text-[9px] font-semibold text-sky-600 uppercase">Blasted This Period</p>
                <p className="text-sm font-bold text-sky-900">{fmt(d.reconSummary.blasted_this)} t</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-2 text-center">
                <p className="text-[9px] font-semibold text-gray-500 uppercase">Blasted Previous</p>
                <p className="text-sm font-bold text-gray-800">{fmt(d.reconSummary.blasted_prev)} t</p>
              </div>
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-2 text-center">
                <p className="text-[9px] font-semibold text-indigo-600 uppercase">Total Blasted</p>
                <p className="text-sm font-bold text-indigo-900">{fmt(d.reconSummary.blasted_total)} t</p>
              </div>
              <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-2 text-center">
                <p className="text-[9px] font-semibold text-cyan-600 uppercase">Moved This Period</p>
                <p className="text-sm font-bold text-cyan-900">{fmt(d.reconSummary.moved_this)} t</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-2 text-center">
                <p className="text-[9px] font-semibold text-gray-500 uppercase">Moved Previous</p>
                <p className="text-sm font-bold text-gray-800">{fmt(d.reconSummary.moved_prev)} t</p>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2 text-center">
                <p className="text-[9px] font-semibold text-emerald-600 uppercase">Total Moved</p>
                <p className="text-sm font-bold text-emerald-900">{fmt(d.reconSummary.moved_total)} t</p>
              </div>
              <div className={`border rounded-lg p-2 text-center ${d.reconSummary.remaining > 0 ? 'bg-red-50 border-red-300' : 'bg-emerald-50 border-emerald-200'}`}>
                <p className={`text-[9px] font-semibold uppercase ${d.reconSummary.remaining > 0 ? 'text-red-600' : 'text-emerald-600'}`}>Remaining Balance</p>
                <p className={`text-sm font-bold ${d.reconSummary.remaining > 0 ? 'text-red-900' : 'text-emerald-900'}`}>{fmt(d.reconSummary.remaining)} t</p>
              </div>
            </div>
          </div>

          {/* Block Detail Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-xs font-semibold text-gray-700 p-3 border-b border-gray-200 uppercase">
              Ore Block Detail ({d.inPitBlocks.length} in pit, {d.blockRecon.filter(b => b.status === 'Partial').length} partial, {d.blockRecon.filter(b => b.status === 'Fully Moved').length} fully moved)
            </h3>
            <div className="overflow-x-auto max-h-96">
              <table className="min-w-full divide-y divide-gray-200 text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr className="border-b border-gray-300">
                    <th rowSpan={2} className="px-2 py-1 text-left font-medium text-gray-500 border-r border-gray-200">Block</th>
                    <th rowSpan={2} className="px-2 py-1 text-left font-medium text-gray-500 border-r border-gray-200">Prd</th>
                    <th colSpan={3} className="px-2 py-1 text-center font-semibold text-sky-700 border-r border-gray-200 bg-sky-50">Blasted (t)</th>
                    <th rowSpan={2} className="px-2 py-1 text-center font-medium text-gray-500 border-r border-gray-200">Grade</th>
                    <th colSpan={3} className="px-2 py-1 text-center font-semibold text-cyan-700 border-r border-gray-200 bg-cyan-50">Moved (t)</th>
                    <th rowSpan={2} className="px-2 py-1 text-center font-medium text-red-500 border-r border-gray-200">Balance</th>
                    <th rowSpan={2} className="px-2 py-1 text-center font-medium text-gray-500">Status</th>
                  </tr>
                  <tr>
                    <th className="px-2 py-1 text-center font-medium text-sky-600 bg-sky-50 text-[9px]">This Prd</th>
                    <th className="px-2 py-1 text-center font-medium text-gray-500 bg-sky-50 text-[9px]">Previous</th>
                    <th className="px-2 py-1 text-center font-medium text-sky-800 bg-sky-50 text-[9px] border-r border-gray-200">Total</th>
                    <th className="px-2 py-1 text-center font-medium text-cyan-600 bg-cyan-50 text-[9px]">This Prd</th>
                    <th className="px-2 py-1 text-center font-medium text-gray-500 bg-cyan-50 text-[9px]">Previous</th>
                    <th className="px-2 py-1 text-center font-medium text-cyan-800 bg-cyan-50 text-[9px] border-r border-gray-200">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {d.blockRecon.map((row, i) => (
                    <tr key={i} className={`hover:bg-yellow-50 ${
                      row.status === 'In Pit' ? 'bg-red-50' : row.status === 'Partial' ? 'bg-amber-50' : i % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                    }`}>
                      <td className="px-2 py-1 font-medium text-gray-800 border-r border-gray-100">{row.BlockID}</td>
                      <td className="px-2 py-1 text-gray-700 border-r border-gray-100">{row.PrdID}</td>
                      <td className="px-2 py-1 text-center text-sky-700 font-medium">{row.blasted_this_t > 0 ? fmt(row.blasted_this_t) : '—'}</td>
                      <td className="px-2 py-1 text-center text-gray-500">{row.blasted_prev_t > 0 ? fmt(row.blasted_prev_t) : '—'}</td>
                      <td className="px-2 py-1 text-center font-semibold text-gray-800 border-r border-gray-100">{fmt(row.blasted_total_t)}</td>
                      <td className="px-2 py-1 text-center text-gray-700 border-r border-gray-100">{row.avg_grade > 0 ? row.avg_grade.toFixed(2) : '—'}</td>
                      <td className="px-2 py-1 text-center text-cyan-700 font-medium">{row.moved_this > 0 ? fmt(row.moved_this) : '—'}</td>
                      <td className="px-2 py-1 text-center text-gray-500">{row.moved_prev > 0 ? fmt(row.moved_prev) : '—'}</td>
                      <td className="px-2 py-1 text-center font-semibold text-gray-800 border-r border-gray-100">{fmt(row.moved_total)}</td>
                      <td className="px-2 py-1 text-center border-r border-gray-100">
                        <span className={`font-bold ${row.remaining > 0 ? 'text-red-700' : 'text-emerald-600'}`}>
                          {fmt(row.remaining)}
                        </span>
                      </td>
                      <td className="px-2 py-1 text-center">
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                          row.status === 'In Pit' ? 'bg-red-100 text-red-700'
                          : row.status === 'Partial' ? 'bg-amber-100 text-amber-700'
                          : 'bg-emerald-100 text-emerald-700'
                        }`}>{row.status}</span>
                      </td>
                    </tr>
                  ))}
                  {/* Totals row */}
                  <tr className="bg-gray-100 font-bold border-t-2 border-gray-300">
                    <td className="px-2 py-1.5 text-gray-800 border-r border-gray-200" colSpan={2}>TOTAL</td>
                    <td className="px-2 py-1.5 text-center text-sky-800">{fmt(d.reconSummary.blasted_this)}</td>
                    <td className="px-2 py-1.5 text-center text-gray-600">{fmt(d.reconSummary.blasted_prev)}</td>
                    <td className="px-2 py-1.5 text-center text-gray-900 border-r border-gray-200">{fmt(d.reconSummary.blasted_total)}</td>
                    <td className="px-2 py-1.5 text-center text-gray-400 border-r border-gray-200">—</td>
                    <td className="px-2 py-1.5 text-center text-cyan-800">{fmt(d.reconSummary.moved_this)}</td>
                    <td className="px-2 py-1.5 text-center text-gray-600">{fmt(d.reconSummary.moved_prev)}</td>
                    <td className="px-2 py-1.5 text-center text-gray-900 border-r border-gray-200">{fmt(d.reconSummary.moved_total)}</td>
                    <td className="px-2 py-1.5 text-center border-r border-gray-200">
                      <span className={d.reconSummary.remaining > 0 ? 'text-red-700' : 'text-emerald-700'}>{fmt(d.reconSummary.remaining)}</span>
                    </td>
                    <td className="px-2 py-1.5 text-center text-gray-500 text-[10px]">{d.blockRecon.length} blocks</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ── ORE FLOW DIAGRAM (Visual) ────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════
function OreFlowDiagram({ data: d }) {
  const totalCrusherFeed = d.totalExPitCrusher + d.totalRompadCrusher;
  const directPct = d.totalMoved > 0 ? (d.totalExPitCrusher / d.totalMoved * 100).toFixed(0) : 0;
  const rompadPct = d.totalMoved > 0 ? (d.totalExPitRompad / d.totalMoved * 100).toFixed(0) : 0;
  const crusherDirectPct = totalCrusherFeed > 0 ? (d.totalExPitCrusher / totalCrusherFeed * 100).toFixed(0) : 0;
  const crusherRomPct = totalCrusherFeed > 0 ? (d.totalRompadCrusher / totalCrusherFeed * 100).toFixed(0) : 0;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
      <h3 className="text-xs font-semibold text-gray-700 border-b border-gray-200 pb-2 mb-3 uppercase">
        Ore Material Flow Diagram (SG = {SG})
      </h3>

      {/* Row 1: PIT source */}
      <div className="flex justify-center mb-1">
        <div className="bg-sky-50 border-2 border-sky-400 rounded-lg px-4 py-2 text-center min-w-[200px]">
          <div className="text-lg">⛏️</div>
          <p className="text-xs font-bold text-sky-800 uppercase">In-Pit Ore Mined</p>
          <p className="text-sm font-bold text-sky-900">{fmt(d.totalOreMinedTonnes)} t</p>
          <p className="text-[10px] text-sky-600">{fmt(d.totalOreMinedVol)} m³ × {SG} SG</p>
        </div>
      </div>

      {/* Arrow down */}
      <div className="flex justify-center"><div className="w-0.5 h-4 bg-gray-400 mx-auto" /><div className="text-gray-400 text-[10px]" /></div>
      <div className="flex justify-center mb-1"><span className="text-[10px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded">Total moved: {fmt(d.totalMoved)} t</span></div>

      {/* Row 2: Distribution — 3 destinations */}
      <div className="grid grid-cols-3 gap-2 mb-1">
        {/* Direct to Crusher */}
        <div className="bg-cyan-50 border-2 border-cyan-400 rounded-lg p-2 text-center">
          <div className="text-base">🚛</div>
          <p className="text-[10px] font-bold text-cyan-800 uppercase">Direct to Crusher</p>
          <p className="text-sm font-bold text-cyan-900">{fmt(d.totalExPitCrusher)} t</p>
          <p className="text-[10px] text-cyan-600">{directPct}% of moved</p>
        </div>
        {/* To ROM Pad */}
        <div className="bg-emerald-50 border-2 border-emerald-400 rounded-lg p-2 text-center">
          <div className="text-base">📦</div>
          <p className="text-[10px] font-bold text-emerald-800 uppercase">To ROM Pad</p>
          <p className="text-sm font-bold text-emerald-900">{fmt(d.totalExPitRompad)} t</p>
          <p className="text-[10px] text-emerald-600">{rompadPct}% of moved</p>
        </div>
        {/* Ore in Pit */}
        <div className={`border-2 rounded-lg p-2 text-center ${d.totalOreInPit > 0 ? 'bg-red-50 border-red-400' : 'bg-gray-50 border-gray-300'}`}>
          <div className="text-base">{d.totalOreInPit > 0 ? '⚠️' : '✅'}</div>
          <p className="text-[10px] font-bold uppercase" style={{ color: d.totalOreInPit > 0 ? '#dc2626' : '#78716c' }}>Ore in Pit</p>
          <p className="text-sm font-bold" style={{ color: d.totalOreInPit > 0 ? '#991b1b' : '#57534e' }}>{fmt(Math.max(0, d.totalOreInPit))} t</p>
          <p className="text-[10px]" style={{ color: d.totalOreInPit > 0 ? '#dc2626' : '#78716c' }}>{d.inPitBlocks.length} blocks</p>
        </div>
      </div>

      {/* Arrows — from Direct + ROM Pad down */}
      <div className="grid grid-cols-3 gap-2 mb-1">
        <div className="flex justify-center"><div className="w-0.5 h-3 bg-cyan-400" /></div>
        <div className="flex justify-center"><div className="w-0.5 h-3 bg-emerald-400" /></div>
        <div />
      </div>

      {/* Row 3: ROM Pad Detail */}
      <div className="grid grid-cols-3 gap-2 mb-1">
        <div /> {/* empty — direct feed skips to crusher */}
        <div className="bg-emerald-50 border border-emerald-300 rounded-lg p-2">
          <p className="text-[10px] font-bold text-emerald-800 uppercase text-center">ROM Pad Stockpile</p>
          <p className="text-xs font-bold text-emerald-900 text-center">{fmt(d.totalRompadInventory)} t</p>
          {d.currentRompad.length > 0 && (
            <div className="mt-1 space-y-0.5">
              {d.currentRompad.map((bin, i) => (
                <div key={i} className="flex justify-between text-[10px]">
                  <span className="flex items-center gap-1">
                    <span className="inline-block w-2 h-2 rounded-sm" style={{ backgroundColor: GRADE_BIN_COLORS[i % GRADE_BIN_COLORS.length] }} />
                    <span className="text-gray-600">{bin.name}</span>
                  </span>
                  <span className="font-medium text-gray-800">{fmt(bin.value)} t</span>
                </div>
              ))}
            </div>
          )}
          <div className="mt-1 pt-1 border-t border-emerald-200 text-center">
            <p className="text-[10px] text-gray-500">To Crusher: <span className="font-bold text-violet-700">{fmt(d.totalRompadCrusher)} t</span></p>
          </div>
        </div>
        <div />
      </div>

      {/* Arrows converging */}
      <div className="grid grid-cols-3 gap-2 mb-1">
        <div className="flex justify-center"><div className="w-0.5 h-3 bg-cyan-400" /></div>
        <div className="flex justify-center"><div className="w-0.5 h-3 bg-violet-400" /></div>
        <div />
      </div>

      {/* Row 4: Crusher */}
      <div className="flex justify-center">
        <div className="bg-amber-50 border-2 border-amber-400 rounded-lg px-4 py-2 text-center min-w-[200px]">
          <div className="text-lg">🏭</div>
          <p className="text-xs font-bold text-amber-800 uppercase">Crusher Feed</p>
          <p className="text-sm font-bold text-amber-900">{fmt(totalCrusherFeed)} t</p>
          <p className="text-[10px] text-amber-600">{crusherDirectPct}% direct + {crusherRomPct}% ROM pad</p>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ── PROCESSING TAB ────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════
function ProcessingTab({ crusherData, crusherTotals: ct, period }) {
  return (
    <div className="p-2 sm:p-3 space-y-3">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <KpiCard title="Crusher Tonnes" actual={`${fmt(ct.act_tonnes)} t`} plan={`${fmt(ct.bgt_tonnes)} t`}
          variance={pct(ct.act_tonnes, ct.bgt_tonnes)} planLabel="Budget" />
        <KpiCard title="Crusher Grade" actual={`${ct.act_avg_grade.toFixed(2)} g/t`} plan={`${ct.bgt_avg_grade.toFixed(2)} g/t`}
          variance={pct(ct.act_avg_grade, ct.bgt_avg_grade)} planLabel="Budget" />
        <KpiCard title="Crusher Content" actual={`${fmt(ct.act_content, 1)}`} plan={`${fmt(ct.bgt_content, 1)}`}
          variance={pct(ct.act_content, ct.bgt_content)} planLabel="Budget" />
        <KpiCard title="Direct Feed" actual={`${fmt(ct.direct_tonnes)} t`}
          plan={ct.act_tonnes > 0 ? `${(ct.direct_tonnes / ct.act_tonnes * 100).toFixed(0)}%` : '—'} planLabel="of Total" />
        <KpiCard title="ROM Pad Feed" actual={`${fmt(ct.rompad_tonnes)} t`}
          plan={ct.act_tonnes > 0 ? `${(ct.rompad_tonnes / ct.act_tonnes * 100).toFixed(0)}%` : '—'} planLabel="of Total" />
        <KpiCard title="Tonnes Variance" actual={`${fmt(ct.act_tonnes - ct.bgt_tonnes)} t`}
          plan={`${pct(ct.act_tonnes, ct.bgt_tonnes)}%`} planLabel="Var" />
      </div>

      {/* Crusher Feed Summary Table — 3 sections */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-xs font-semibold text-gray-700 p-3 border-b border-gray-200 uppercase">Crusher Feed Summary</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50"><tr>
              <th className="px-2 py-1.5 text-left font-medium text-gray-500">Metric</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">Actual</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">Budget</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">Variance</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">%</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-100">
              {/* Section 1: ExPit Direct Feed */}
              <tr className="bg-cyan-50"><td colSpan={5} className="px-2 py-1.5 font-semibold text-cyan-800 text-[10px] uppercase tracking-wider">1. ExPit Direct Feed (Pit → Crusher)</td></tr>
              <SummaryRow label="Tonnes" actual={ct.direct_tonnes} plan={0} unit="t" />
              <SummaryRow label="Grade" actual={ct.direct_avg_grade} plan={0} unit="g/t" dec={2} />
              <SummaryRow label="Content" actual={ct.direct_content} plan={0} unit="" dec={1} />
              {/* Section 2: Rompad to Crusher */}
              <tr className="bg-violet-50"><td colSpan={5} className="px-2 py-1.5 font-semibold text-violet-800 text-[10px] uppercase tracking-wider">2. ROM Pad to Crusher (Stockpile → Crusher)</td></tr>
              <SummaryRow label="Tonnes" actual={ct.rompad_tonnes} plan={0} unit="t" />
              <SummaryRow label="Grade" actual={ct.rompad_avg_grade} plan={0} unit="g/t" dec={2} />
              <SummaryRow label="Content" actual={ct.rompad_content} plan={0} unit="" dec={1} />
              {/* Section 3: Total Crusher Feed */}
              <tr className="bg-amber-50"><td colSpan={5} className="px-2 py-1.5 font-semibold text-amber-800 text-[10px] uppercase tracking-wider">3. Total Crusher Feed (Combined)</td></tr>
              <SummaryRow label="Tonnes" actual={ct.act_tonnes} plan={ct.bgt_tonnes} unit="t" />
              <SummaryRow label="Grade" actual={ct.act_avg_grade} plan={ct.bgt_avg_grade} unit="g/t" dec={2} />
              <SummaryRow label="Content" actual={ct.act_content} plan={ct.bgt_content} unit="" dec={1} />
            </tbody>
          </table>
        </div>
      </div>

      {/* Tonnes chart — stacked: Direct + Rompad vs Budget */}
      <ChartCard title={`Crusher Feed Tonnes — Actual (Direct + ROM) vs Budget by ${period}`}>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={crusherData} margin={{ top: 10, right: 10, bottom: 20, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="period" tick={{ fontSize: 10, fill: '#6b7280' }} angle={-30} textAnchor="end" height={60} />
            <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} tickFormatter={v => v.toLocaleString()} />
            <Tooltip content={<CustomTooltip unit="t" />} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="direct_tonnes" name="Direct Feed" stackId="actual" fill={COLORS.directCrusher} />
            <Bar dataKey="rompad_tonnes" name="ROM Pad Feed" stackId="actual" fill={COLORS.fromRompad} />
            <Bar dataKey="bgt_tonnes" name="Budget" fill={COLORS.budget} radius={[2,2,0,0]} opacity={0.5} />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Grade chart */}
      <ChartCard title={`Crusher Feed Grade — Actual vs Budget by ${period} (g/t)`}>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={crusherData} margin={{ top: 10, right: 10, bottom: 20, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="period" tick={{ fontSize: 10, fill: '#6b7280' }} angle={-30} textAnchor="end" height={60} />
            <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} tickFormatter={v => v.toFixed(2)} />
            <Tooltip content={<CustomTooltip unit="g/t" dec={2} />} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="act_avg_grade" name="Actual Grade" fill={COLORS.actual} radius={[2,2,0,0]} />
            <Bar dataKey="bgt_avg_grade" name="Budget Grade" fill={COLORS.budget} radius={[2,2,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Content chart */}
      <ChartCard title={`Crusher Feed Content — Actual vs Budget by ${period}`}>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={crusherData} margin={{ top: 10, right: 10, bottom: 20, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="period" tick={{ fontSize: 10, fill: '#6b7280' }} angle={-30} textAnchor="end" height={60} />
            <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} tickFormatter={v => v.toLocaleString()} />
            <Tooltip content={<CustomTooltip unit="" dec={1} />} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="act_content" name="Actual Content" fill={COLORS.actual} radius={[2,2,0,0]} />
            <Bar dataKey="bgt_content" name="Budget Content" fill={COLORS.budget} radius={[2,2,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Period detail table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-xs font-semibold text-gray-700 p-3 border-b border-gray-200 uppercase">Crusher Feed — Period Detail</h3>
        <div className="overflow-x-auto max-h-80">
          <table className="min-w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50 sticky top-0"><tr>
              <th className="px-2 py-1.5 text-left font-medium text-gray-500">Period</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">Direct (t)</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">ROM (t)</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">Act Total</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">Bgt Tonnes</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">T Var %</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">Act Grade</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">Bgt Grade</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">G Var %</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">Act Content</th>
              <th className="px-2 py-1.5 text-center font-medium text-gray-500">Bgt Content</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-100">
              {crusherData.map((row, i) => {
                const tVar = row.bgt_tonnes > 0 ? ((row.act_tonnes - row.bgt_tonnes) / row.bgt_tonnes * 100) : 0;
                const gVar = row.bgt_avg_grade > 0 ? ((row.act_avg_grade - row.bgt_avg_grade) / row.bgt_avg_grade * 100) : 0;
                return (
                  <tr key={i} className={`hover:bg-yellow-50 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                    <td className="px-2 py-1 font-medium text-gray-800">{row.period}</td>
                    <td className="px-2 py-1 text-center text-cyan-700">{fmt(row.direct_tonnes)}</td>
                    <td className="px-2 py-1 text-center text-violet-700">{fmt(row.rompad_tonnes)}</td>
                    <td className="px-2 py-1 text-center font-semibold text-gray-800">{fmt(row.act_tonnes)}</td>
                    <td className="px-2 py-1 text-center text-gray-700">{fmt(row.bgt_tonnes)}</td>
                    <td className="px-2 py-1 text-center"><VarianceBadge value={tVar} /></td>
                    <td className="px-2 py-1 text-center text-gray-700">{row.act_avg_grade.toFixed(2)}</td>
                    <td className="px-2 py-1 text-center text-gray-700">{row.bgt_avg_grade.toFixed(2)}</td>
                    <td className="px-2 py-1 text-center"><VarianceBadge value={gVar} /></td>
                    <td className="px-2 py-1 text-center text-gray-700">{fmt(row.act_content, 1)}</td>
                    <td className="px-2 py-1 text-center text-gray-700">{fmt(row.bgt_content, 1)}</td>
                  </tr>
                );
              })}
              {crusherData.length === 0 && <tr><td colSpan={11} className="px-4 py-6 text-center text-gray-400">No data</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ── Shared Sub-components ─────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════

function KpiCard({ title, actual, plan, variance, planLabel = 'Plan' }) {
  const v = parseFloat(variance);
  const hasVariance = variance && variance !== '—';
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2.5">
      <p className="text-[10px] font-semibold text-gray-500 uppercase truncate">{title}</p>
      <div className="flex items-baseline gap-1.5 mt-0.5">
        <span className="text-sm sm:text-base font-bold text-gray-900">{actual}</span>
        {plan && plan !== '—' && (
          <span className="text-[10px] text-gray-400">vs <span className="font-medium text-gray-500">{plan}</span></span>
        )}
      </div>
      <div className="flex items-center gap-1.5 mt-0.5">
        <span className="text-[10px] text-gray-400">{planLabel}</span>
        {hasVariance && (
          <span className={`inline-block px-1 py-0 rounded text-[10px] font-semibold ${v > 0 ? 'bg-indigo-100 text-indigo-700' : v < 0 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
            {v > 0 ? '▲' : v < 0 ? '▼' : '●'} {variance}%
          </span>
        )}
      </div>
    </div>
  );
}

function ComplianceCard({ title, value, sub, color }) {
  const borderMap = { indigo: 'border-indigo-400', amber: 'border-amber-400', stone: 'border-stone-400' };
  const textMap = { indigo: 'text-indigo-700', amber: 'text-amber-700', stone: 'text-stone-600' };
  const bgMap = { indigo: 'bg-indigo-50', amber: 'bg-amber-50', stone: 'bg-stone-50' };
  return (
    <div className={`rounded-lg shadow-sm border-2 p-2.5 ${borderMap[color] || 'border-gray-200'} ${bgMap[color] || 'bg-white'}`}>
      <p className="text-[10px] font-semibold text-gray-500 uppercase truncate">{title}</p>
      <p className={`text-lg sm:text-xl font-bold mt-0.5 ${textMap[color] || 'text-gray-900'}`}>{value}</p>
      <p className="text-[10px] text-gray-500 mt-0.5">{sub}</p>
    </div>
  );
}

function SummaryRow({ label, actual, plan, unit, dec = 0 }) {
  const diff = actual - plan;
  const pctVal = plan > 0 ? ((diff / plan) * 100).toFixed(1) : '—';
  const isPos = diff > 0;
  return (
    <tr className="hover:bg-gray-50">
      <td className="px-2 py-1.5 font-medium text-gray-800">{label}</td>
      <td className="px-2 py-1.5 text-center text-gray-700">{fmt(actual, dec)} {unit}</td>
      <td className="px-2 py-1.5 text-center text-gray-700">{fmt(plan, dec)} {unit}</td>
      <td className={`px-2 py-1.5 text-center font-medium ${isPos ? 'text-indigo-700' : 'text-amber-700'}`}>
        {isPos ? '+' : ''}{fmt(diff, dec)} {unit}
      </td>
      <td className="px-2 py-1.5 text-center">
        {pctVal !== '—' ? <VarianceBadge value={parseFloat(pctVal)} /> : '—'}
      </td>
    </tr>
  );
}

function VarianceBadge({ value }) {
  const isPos = value > 0;
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${isPos ? 'bg-indigo-100 text-indigo-700' : 'bg-amber-100 text-amber-700'}`}>
      {isPos ? '+' : ''}{value.toFixed(1)}%
    </span>
  );
}

function TypeBadge({ type }) {
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${type === 'Ore' ? 'bg-sky-100 text-sky-700' : 'bg-stone-100 text-stone-600'}`}>
      {type}
    </span>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <h3 className="text-xs font-semibold text-gray-700 p-3 border-b border-gray-200">{title}</h3>
      <div className="p-2">{children}</div>
    </div>
  );
}

function CustomTooltip({ active, payload, label, unit = '', dec = 0 }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 rounded-lg p-3 shadow-lg border border-slate-700">
      <p className="text-white text-xs font-semibold mb-1">{label}</p>
      {payload.map((item, i) => (
        <p key={i} className="text-xs" style={{ color: item.color || '#94a3b8' }}>
          {item.name}: <span className="font-medium text-white">{Number(item.value).toLocaleString('en', { maximumFractionDigits: dec > 0 ? dec : 2 })} {unit}</span>
        </p>
      ))}
    </div>
  );
}

function ComplianceTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 rounded-lg p-3 shadow-lg border border-slate-700">
      <p className="text-white text-xs font-semibold mb-1">{label}</p>
      {payload.map((item, i) => (
        <p key={i} className="text-xs" style={{ color: item.color || '#94a3b8' }}>
          {item.name}: <span className="font-medium text-white">
            {item.name.includes('%') ? `${Number(item.value).toFixed(1)}%` : item.value}
          </span>
        </p>
      ))}
    </div>
  );
}
